/**
 * AuditLogStatistics Component
 *
 * Displays summary statistics for audit logs
 * Shows total entries, operations by type, most active users, and date range
 *
 * Related Story: WORKFORCE-V2-story-2.004
 */

import React, { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import {
  Activity,
  Users,
  Calendar,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  BarChart3
} from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { AuditLogEntry } from '@/lib/services/audit-log-api'
import { cn } from '@/lib/utils'

interface AuditLogStatisticsProps {
  logs: AuditLogEntry[]
}

export function AuditLogStatistics({ logs }: AuditLogStatisticsProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)

  /**
   * Calculate statistics from logs
   */
  const statistics = useMemo(() => {
    // Total entries
    const totalEntries = logs.length

    // Operations by type
    const operationCounts = logs.reduce((acc, log) => {
      acc[log.operation] = (acc[log.operation] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    // User activity counts
    const userCounts = logs.reduce((acc, log) => {
      acc[log.userName] = (acc[log.userName] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    // Top 5 most active users
    const topUsers = Object.entries(userCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([userName, count]) => ({ userName, count }))

    // Entity type distribution
    const entityTypeCounts = logs.reduce((acc, log) => {
      acc[log.entityType] = (acc[log.entityType] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    // Date range
    let dateRange: { earliest: Date; latest: Date } | null = null
    if (logs.length > 0) {
      const timestamps = logs.map(log => new Date(log.timestamp).getTime())
      const earliest = new Date(Math.min(...timestamps))
      const latest = new Date(Math.max(...timestamps))
      dateRange = { earliest, latest }
    }

    return {
      totalEntries,
      operationCounts,
      topUsers,
      entityTypeCounts,
      dateRange
    }
  }, [logs])

  /**
   * Format date for display
   */
  const formatDate = (date: Date) => {
    return format(date, 'dd/MM/yyyy', { locale: ptBR })
  }

  /**
   * Get entity type color
   */
  const getEntityTypeColor = (entityType: string) => {
    switch (entityType) {
      case 'EMPLOYEE':
        return 'bg-blue-500/20 text-blue-400'
      case 'PAYROLL':
        return 'bg-green-500/20 text-green-400'
      case 'DEPARTMENT':
        return 'bg-purple-500/20 text-purple-400'
      case 'ROLE':
        return 'bg-orange-500/20 text-orange-400'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  if (logs.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-brand-primary" />
            Estatísticas
          </CardTitle>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="gap-2"
          >
            {isCollapsed ? (
              <>
                <ChevronDown className="w-4 h-4" />
                Expandir
              </>
            ) : (
              <>
                <ChevronUp className="w-4 h-4" />
                Recolher
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Entries */}
            <Card className="bg-brand-dark">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-brand-primary/20 rounded-lg">
                    <Activity className="w-6 h-6 text-brand-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-brand-gray">Total de Entradas</p>
                    <p className="text-2xl font-bold text-white font-display">
                      {statistics.totalEntries.toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Operations Count */}
            <Card className="bg-brand-dark">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-brand-gray">Tipos de Operação</p>
                    <p className="text-2xl font-bold text-white font-display">
                      {Object.keys(statistics.operationCounts).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Active Users */}
            <Card className="bg-brand-dark">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-brand-gray">Usuários Ativos</p>
                    <p className="text-2xl font-bold text-white font-display">
                      {statistics.topUsers.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Date Range */}
            {statistics.dateRange && (
              <Card className="bg-brand-dark">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-500/20 rounded-lg">
                      <Calendar className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-brand-gray">Período</p>
                      <p className="text-sm text-white font-mono">
                        {formatDate(statistics.dateRange.earliest)}
                      </p>
                      <p className="text-sm text-white font-mono">
                        {formatDate(statistics.dateRange.latest)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Detailed Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Top Operations */}
            <Card className="bg-brand-dark">
              <CardHeader>
                <h4 className="font-display font-semibold text-white text-sm">
                  Operações Principais
                </h4>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(statistics.operationCounts)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 5)
                    .map(([operation, count]) => (
                      <div
                        key={operation}
                        className="flex items-center justify-between p-2 rounded bg-brand-gray/5"
                      >
                        <span className="text-sm text-white truncate">{operation}</span>
                        <Badge className="bg-brand-primary/20 text-brand-primary">
                          {count}
                        </Badge>
                      </div>
                    ))}

                  {Object.keys(statistics.operationCounts).length === 0 && (
                    <p className="text-sm text-brand-gray text-center py-4">
                      Nenhuma operação registrada
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Top Users */}
            <Card className="bg-brand-dark">
              <CardHeader>
                <h4 className="font-display font-semibold text-white text-sm">
                  Usuários Mais Ativos
                </h4>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {statistics.topUsers.map(({ userName, count }, index) => (
                    <div
                      key={userName}
                      className="flex items-center justify-between p-2 rounded bg-brand-gray/5"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-xs font-bold text-brand-primary">#{index + 1}</span>
                        <span className="text-sm text-white truncate">{userName}</span>
                      </div>
                      <Badge className="bg-blue-500/20 text-blue-400">
                        {count}
                      </Badge>
                    </div>
                  ))}

                  {statistics.topUsers.length === 0 && (
                    <p className="text-sm text-brand-gray text-center py-4">
                      Nenhum usuário ativo
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Entity Type Distribution */}
            <Card className="bg-brand-dark">
              <CardHeader>
                <h4 className="font-display font-semibold text-white text-sm">
                  Distribuição por Tipo
                </h4>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(statistics.entityTypeCounts)
                    .sort(([, a], [, b]) => b - a)
                    .map(([entityType, count]) => (
                      <div
                        key={entityType}
                        className="flex items-center justify-between p-2 rounded bg-brand-gray/5"
                      >
                        <Badge className={getEntityTypeColor(entityType)}>
                          {entityType}
                        </Badge>
                        <span className="text-sm font-medium text-white">
                          {count} ({Math.round((count / statistics.totalEntries) * 100)}%)
                        </span>
                      </div>
                    ))}

                  {Object.keys(statistics.entityTypeCounts).length === 0 && (
                    <p className="text-sm text-brand-gray text-center py-4">
                      Nenhum tipo de entidade
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      )}
    </Card>
  )
}
